import React, { useState, useEffect, useCallback } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useAuth } from "../../hooks/use-auth";
import { Flex } from "@fluentui/react-northstar";
import LoginPageTemplate from "../../components/templates/LoginPageTemplate";
import { useMst } from "../../hooks/useMst";
import {
  initData,
  getPermissions,
  getPasswordHints,
  getAccountProfile,
  getCustomValue,
  setCustomValue,
} from "./controller";
import { applySnapshot } from "mobx-state-tree";
import Constants from "../../constants/text-constants";
import SignalR from '../../services/SignalR/signalR';
import ErrorDialog from "../../components/atoms/ErrorDialog";
import CustomValues from "../../constants/custom-values";
import LoginPageStates from "../../constants/loginpage-states";
import { appInsights } from "../../config/analytics/AppInsights";

const LoginPage = (props) => {
  const [state, setState] = useState({
    username: "",
    password: "",
    loginFailed: false,
    dataLoadFailed: false,
    loadingText: Constants.CHECKING_CREDENTIALS,
    showNoPermissionDialog: false,
    contentToShow: LoginPageStates.SIGNIN,
  });
  const firstLoad = React.useRef(true);
  const firstname = React.useRef("");

  const {
    permissionStore,
    currentUser,
    agentStore,
    reasonCodeStore,
    statusCodeStore,
    teamStore,
    locationStore,
    passwordHintStore,
  } = useMst();

  let history = useHistory();
  let location = useLocation();
  let auth = useAuth();

  const hasMinimumPermissions = () => {
    // This is the minimum set of permissions required to log in to TCM
    return permissionStore.havePermissions("Agent", ["MyDetail"]) &&
      permissionStore.havePermissions("Accounts", ["About"]) &&
      permissionStore.havePermissions("Call Manager", ["PutStatus", "PutReason"]) &&
      permissionStore.havePermissions("Status Codes", ["GetStatus"]) &&
      permissionStore.havePermissions("Reason Codes", ["GetAll"]);
  }

  const validateAgentList = (locations, currentAgent, agents) => {
    // only set the current agent location if it exists in the store (they might not have permissions to get locations)
    const loc = locations.find(
      (l) => l.code === currentAgent.locationCode
    );

    // Remove any teams from all agents that are not the current agents teams
    for (let agent of agents) {
      agent.teams = agent.teams.filter((team) => teamStore.isValidTeam(team.id));

      // only set the location code for the current user and if it exists in the store
      if (agent.id === currentAgent.id && loc) {
        agent.locationCode = loc.code;
      } else {
        agent.locationCode = null;
      }
    }
  }

  const checkChangePassword = (changePassword) => {
    getPasswordHints().then((res) => {
      passwordHintStore.setPasswordHints(res);

      if (changePassword) {
        // show change password screen
        setState((prevState) => ({
          ...prevState,
          contentToShow: LoginPageStates.CHANGE_PASSWORD,
        }));
      } else {
        loadInitialData();
      }
    }).catch((e) => {
      showErrorDialog();
    });
  };

  const handleChangePasswordComplete = () => {
    setState((prevState) => ({
      ...prevState,
      changePassword: false,
    }));

    loadInitialData();
  };

  const handleChangeTellAboutYouOnboard = ({onboardingComplete, firstName}) => {
    firstname.current = firstName;

    setState((prevState) => ({
      ...prevState,
      contentToShow: LoginPageStates.LOADING,
    }));

    if (onboardingComplete && permissionStore.havePermissions("Custom Values", [
      "Set"
    ])) {
      setCustomValue(CustomValues.CV_SKIP_TELL_US_ABOUT_YOU, "true", permissionStore);
    }
    initialiseStores();
  };

  const teamsCallingLocationComplete = () => {
    setState((prevState) => ({
      ...prevState,
      contentToShow: LoginPageStates.LOADING,
    }));

    setCustomValue(CustomValues.CV_SKIP_MS_LOCATION, "true", permissionStore);

    loadCallAssistant();
  };

  const checkTellUsAboutYou = async () => {
    let onboardingComplete = false;
    const accountProfileResp = await getAccountProfile();
    firstname.current = accountProfileResp.firstname;

    const otuay = await getCustomValue(CustomValues.CV_SKIP_TELL_US_ABOUT_YOU, permissionStore);

    if (otuay && otuay.value === "true") {
      onboardingComplete = true;
    }
    return !onboardingComplete;
  }

  const doesMSTeamLocationExist = () => {
    const msTeamslocation = locationStore.locations.find((loc) => loc.description === Constants.MICROSOFT_LOCATION);

    return msTeamslocation ? true : false;
  }

  const checkMSLocation = async () => {

    let skipped = false;

    const hasLocationsPermissions = permissionStore.havePermissions("Locations", [
      "Create",
      "GetAll",
    ]);

    if (!hasLocationsPermissions) {
      loadCallAssistant();
      return;
    }

    const mslocation = await getCustomValue(CustomValues.CV_SKIP_MS_LOCATION, permissionStore);

    if (mslocation && mslocation.value === "true") {
      skipped = true;
    }

    if (skipped || doesMSTeamLocationExist()) {
      loadCallAssistant();
    } else {
      setState((prevState) => ({
        ...prevState,
        contentToShow: LoginPageStates.TEAMSCALLINGLOCATION,
      }));
    }
  }

  const loadInitialData = () => {
    setState((prevState) => ({
      ...prevState,
      contentToShow: LoginPageStates.LOADING,
      loadingText: Constants.LAUNCHING_CCC,
    }));

    getPermissions().then((response) => {
      permissionStore.setPermissions(response);

      if (!hasMinimumPermissions()) {
        // one or all of the required permissions to use TCM is missing
        //show permission failed login dialog

        // Sign this user out
        auth.signout();

        // Display the no permission error dialog
        showNoPermissionsDialog();
      } else {
        checkTellUsAboutYou().then((checkTellUs) => {
          if (!checkTellUs) {
            initialiseStores();
          } else {
            // go to tell us about you page
            setState((prevState) => ({
              ...prevState,
              contentToShow: LoginPageStates.TELLUSABOUTYOU,
            }));
          }
        }).catch((err) => {
          // Check tell us about you threw an error
          showErrorDialog();
        });
      }
    }).catch((e) => {
      //getpermissions call threw an exception - show error dialog
      showErrorDialog();
    });
  };

  const initialiseStores = () => {
    initData(permissionStore)
      .then((resp) => {
        // get all agents, teams, reason codes, status codes and locations and add to store
        const {
          agents,
          reasonCodes,
          statusCodes,
          locations,
          about,
          currentAgent,
          passwordHints,
        } = resp;

        reasonCodeStore.setReasonCodes(reasonCodes);
        statusCodeStore.setStatusCodes(statusCodes);
        // setTeams must be called before setAgents
        teamStore.setTeams(currentAgent.teams);
        locationStore.setLocations(locations);
        passwordHintStore.setPasswordHints(passwordHints);

        // make sure agents list contains correct data before trying to use it in setAgents
        validateAgentList(locations, currentAgent, agents);

        // If the agents list is blank (probably because user doesn't have CallManager->AgentMonitor permission)
        // then set the current user as the only agent in the list
        if (!agents.length) {
          agentStore.setAgents([currentAgent]);
        } else {
          agentStore.setAgents(agents);
        }

        // store about details
        applySnapshot(currentUser, about);

        // then set currentUser agent
        currentUser.setAgent(currentAgent.id);

        // Call details are only available in currentAgent and SignalR updates
        currentUser.agent.setCallDetails(currentAgent);
        currentUser.agent.setAlertTime(currentAgent.alertTime);

        checkMSLocation();
      }).catch((err) => {
        showErrorDialog();
      });


  }


  const loadCallAssistant = () => {
    SignalR.start(agentStore, teamStore, currentUser, permissionStore);
    auth.setReAuthCallback((token) => {
      SignalR.setAccessToken(token);
    });

    let { from } = location.state || {
      from: {
        pathname: "/call-assistant",
        state: { firstname: firstname.current },
      },
    };

    history.replace(from);
  }

  const handleForgotPassword = () => {
    history.replace({
      pathname: "/forgotten-password",
      state: { passedInUsername: state.username },
    });
  };

  const handleLogin = () => {
    setState((prevState) => ({
      ...prevState,
      loadingText: Constants.CHECKING_CREDENTIALS,
      contentToShow: LoginPageStates.LOADING,
    }));

    auth.signin(state.username, state.password, (loginResp) => {
      if (loginResp.Success) {
        checkChangePassword(loginResp.ChangePassword);
      } else {
        //show failed login dialog
        showFailedLoginDialog();
      }
    });
  };

  const handleFieldChange = (id, value) => {
    setState((prevState) => ({ ...prevState, [id]: value }));
  };

  const handleLoginDialogConfirm = () => {
    setState((prevState) => ({ ...prevState, loginFailed: false }));
  };

  const handlePermissionErrorConfirm = () => {
    setState((prevState) => ({ ...prevState, showNoPermissionDialog: false }));
  };

  const handleDataFailedDialogConfirm = () => {
    setState((prevState) => ({ ...prevState, dataLoadFailed: false }));
  };

  const showNoPermissionsDialog = () => {
    // Display the no permission error dialog
    setState((prevState) => ({
      ...prevState,
      showNoPermissionDialog: true,
      contentToShow: LoginPageStates.SIGNIN,
      loadingText: Constants.CHECKING_CREDENTIALS,
    }));
  };

  const showErrorDialog = () => {
    auth.signout();

    // show error dialog
    setState((prevState) => ({
      ...prevState,
      dataLoadFailed: true,
      contentToShow: LoginPageStates.SIGNIN,
      loadingText: Constants.CHECKING_CREDENTIALS,
    }));
  }

  const showFailedLoginDialog = () => {
    //show failed login dialog
    setState((prevState) => ({
      ...prevState,
      loginFailed: true,
      contentToShow: LoginPageStates.SIGNIN,
    }));
  }

  const getErrorDialog = () => {
    let dialog = null;

    if (state.loginFailed) {
      dialog = (
        <ErrorDialog
          onConfirm={handleLoginDialogConfirm}
          title={Constants.UNSUCCESSFUL_LOGIN}
          text={Constants.INVALID_CREDENTIALS}
          testId="loginFailedDialog"
        />
      );
    } else if (state.showNoPermissionDialog) {
      dialog = (
        <ErrorDialog
          onConfirm={handlePermissionErrorConfirm}
          title={Constants.UNSUCCESSFUL_LOGIN}
          text={Constants.NO_PERMISSIONS_LOGIN}
          testId="noPermissionDialog"
        />
      );
    } else if (state.dataLoadFailed) {
      dialog = (
        <ErrorDialog
          onConfirm={handleDataFailedDialogConfirm}
          title={Constants.INITIAL_DATA_LOAD_ERROR_TITLE}
          text={Constants.INITIAL_DATA_LOAD_ERROR_CONTENT}
          testId="dataLoadingFailedDialog"
        />
      );
    }

    return dialog;
  }

  // Make a callback out of loadInitialData so it is not recreated on every render
  const loadInitialDataCb = useCallback(loadInitialData, []);
  const isValidToken = useCallback(auth.isValidToken, []);

  const init = useCallback(() => {
    // If we have valid token, and we're showing the loading spinner
    if (isValidToken() && state.contentToShow === LoginPageStates.LOADING) {
      // user has successfully logged in - set the authenticated user in app insights
      const username = localStorage.getItem('username');
      appInsights.setAuthenticatedUserContext(username, null, true);

      loadInitialDataCb();
    }
  }, [isValidToken, loadInitialDataCb, state.contentToShow]);

  // Only call init once, right at the start, after the first render
  useEffect(init, []);

  if (auth.isValidToken() && firstLoad.current) {
    // If we have stored credentials, and are authenticated, and this is BEFORE the first render, 
    // then display the loading spinner - loadInitialData will be called by 'init' if this is the first render.
    // This stops the signin box appearing breifly before the loading spinner
    setState((prevState) => ({ ...prevState, contentToShow: LoginPageStates.LOADING }));
  }

  firstLoad.current = false;

  let errorDialog = getErrorDialog();

  return (
    <Flex>
      <LoginPageTemplate
        username={state.username}
        password={state.password}
        onChange={handleFieldChange}
        signInClicked={handleLogin}
        forgotPasswordClicked={handleForgotPassword}
        errorDialog={errorDialog}
        loadingText={state.loadingText}
        changePasswordComplete={handleChangePasswordComplete}
        tellAboutYouOnboardComplete={handleChangeTellAboutYouOnboard}
        contentToShow={state.contentToShow}
        teamsCallingLocationComplete={teamsCallingLocationComplete}
      />
    </Flex>
  );
};

export default LoginPage;